import { useEffect, useState, useRef, useCallback } from "react";
import { Row, Col, Form, Button, Modal, Card } from "react-bootstrap";
import { http } from "../auth";
import { MdInfo, MdNetworkCheck, MdOutlineFactory, MdStore } from "react-icons/md";
import { AiOutlineGlobal } from "react-icons/ai";
import React, { memo } from "react";
import { useThemeManager } from "../Hooks/ThemeManager";
import { toast } from "react-toastify";
import { IoCalendarOutline, IoFingerPrintOutline, IoInformationCircleOutline } from "react-icons/io5";
import { MdOutlineAccountBalance, MdOutlineNumbers, MdOutlinePayments } from "react-icons/md";
import { useNavigate } from "react-router-dom";

const Abonar = ({
  data,
  loading,
  type,
  bank,
  onSelectedIdsChange,
  list,
  disabled,
  show,
  selectedMetodo,
  selectedBank,
  setSelectedMetodo,
  selectedCurrency,
  onTotalChange,
}) => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [abonar, setAbonar] = useState({
    total_a_pagar: "0.00",
    igtf: "0.00",
    iva: "0.00",
    comision: "0.00",
    abonos: [],
  });
  const [loadingResume, setLoadingResume] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pointer, setPointer] = useState(true);
  const [loadingIcons, setLoadingIcons] = useState({});
  const { themeColors } = useThemeManager();
  const [showModal, setShowModal] = useState(false);
  const [selectedAbonoInfo, setSelectedAbonoInfo] = useState([]);

  // Refs para controlar las llamadas
  const mountedRef = useRef(true);
  const consultaTimeoutRef = useRef(null);
  const consultaKeyRef = useRef(null);
  const isInitializedRef = useRef(false);
  const previousMetodoRef = useRef(null);
  const isCalculatingRef = useRef(false); // Para evitar ejecuciones simultáneas
  const lastExecutionRef = useRef(null); // Para controlar la última ejecución

  // Función para formatear números
  const formatNumber = (value) => {
    if (value === undefined || value === null || value === "") return "0.00";
    const num = parseFloat(value);
    if (isNaN(num)) return "0.00";
    return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (consultaTimeoutRef.current) {
        clearTimeout(consultaTimeoutRef.current);
      }
    };
  }, []);

  const handleCheckboxChange = async (id, isChecked) => {
    const updatedIds = isChecked ? [...selectedIds, id] : selectedIds.filter((itemId) => itemId !== id);
    setSelectedIds(updatedIds);
    if (onSelectedIdsChange) {
      onSelectedIdsChange(updatedIds);
    }
  };

  // Función para obtener el método como string
  const getMetodoString = (metodo) => {
    if (!metodo) return null;
    if (typeof metodo === "string") return metodo;
    if (typeof metodo === "object") {
      return metodo.type || metodo.tipo || metodo.nombre || null;
    }
    return null;
  };

  // Función para resetear el estado de abono
  const resetAbono = useCallback(() => {
    setAbonar({
      total_a_pagar: "0.00",
      igtf: "0.00",
      iva: "0.00",
      comision: "0.00",
      abonos: [],
    });
    setLoadingResume(true);
    consultaKeyRef.current = null;
    isCalculatingRef.current = false;
  }, []);

  // Función para calcular abono con control de duplicados
  const handleCalculateAbono = useCallback(async (ids, metodo, bank, currency) => {
    // Evitar ejecuciones simultáneas
    if (isCalculatingRef.current) {
      return;
    }

    const metodoStr = getMetodoString(metodo);
    
    // Si cambió el método, resetear
    const previousMetodo = previousMetodoRef.current;
    if (previousMetodo && previousMetodo !== metodoStr) {
      resetAbono();
      previousMetodoRef.current = metodoStr;
      // Limpiar clave de consulta para forzar nueva
      consultaKeyRef.current = null;
    }

    if (!metodoStr) {
      resetAbono();
      return;
    }

    if (!ids || ids.length === 0) {
      resetAbono();
      return;
    }

    // Validar dependencias según el método
    const needsBank = metodoStr === "c2p" || metodoStr === "p2p";
    if (needsBank && (!bank || bank === "")) {
      setLoadingResume(false);
      return;
    }

    const needsCurrency = metodoStr === "transferencia" || metodoStr === "transfer";
    if (needsCurrency && (!currency || currency === "")) {
      setLoadingResume(false);
      return;
    }

    // Crear clave única para la consulta
    const consultaKey = `${ids.join(',')}_${metodoStr}_${bank || ''}_${currency || ''}`;
    
    // Si ya se consultó con estos parámetros, no hacer nada
    if (consultaKeyRef.current === consultaKey) {
      return;
    }

    // Marcar que estamos calculando
    isCalculatingRef.current = true;
    consultaKeyRef.current = consultaKey;
    setLoadingResume(false);

    const payload = {
      id_services: ids,
      type: metodoStr,
    };

    if (bank && bank !== "") payload.bank = bank;
    if (currency && currency !== "") payload.currency = currency;


    // Limpiar timeout anterior
    if (consultaTimeoutRef.current) {
      clearTimeout(consultaTimeoutRef.current);
    }

    // Delay para evitar múltiples llamadas
    consultaTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await http.post("/intranet-j/consultar/abono", payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (mountedRef.current) {
          setAbonar({
            total_a_pagar: response.data.total_a_pagar || "0.00",
            igtf: response.data.igtf || "0.00",
            iva: response.data.iva || "0.00",
            comision: response.data.comision || "0.00",
            base: response.data.base || "0.00",
            monto_abonado: response.data.monto_abonado,
            monto_retenido: response.data.monto_retenido,
            abonos: response.data.abonos || [],
          });
          setLoadingResume(true);
          isInitializedRef.current = true;
          previousMetodoRef.current = metodoStr;
          lastExecutionRef.current = Date.now();
        }
      } catch (err) {
        if (!mountedRef.current) return;
        
        toast.error(err.response?.data?.detail || "Error al calcular el abono");

        if (err.response?.status === 400) {
          if (ids.length > 1) {
            let problematicId = null;
            for (const id of ids) {
              try {
                await http.post(
                  "/intranet-j/consultar/abono",
                  {
                    id_services: [id],
                    bank: bank || null,
                    type: metodoStr || null,
                    currency: currency || null
                  },
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                  },
                );
              } catch (individualErr) {
                if (individualErr.response?.status === 400) {
                  problematicId = id;
                  break;
                }
              }
            }

            if (problematicId) {
              setSelectedIds((prevIds) => prevIds.filter((id) => id !== problematicId));
              if (onSelectedIdsChange) {
                const newIds = ids.filter((id) => id !== problematicId);
                onSelectedIdsChange(newIds);
              }
            } else {
              setSelectedIds([]);
              if (onSelectedIdsChange) onSelectedIdsChange([]);
            }
          } else {
            const problematicId = ids[0];
            setSelectedIds((prevIds) => prevIds.filter((id) => id !== problematicId));
            if (onSelectedIdsChange) onSelectedIdsChange([]);
          }
        }
        consultaKeyRef.current = null;
        setLoadingResume(true);
      } finally {
        if (mountedRef.current) {
          isCalculatingRef.current = false;
        }
      }
    }, 300);
  }, [token, onSelectedIdsChange, resetAbono]);

  // Efecto para selección inicial de IDs (solo una vez)
  useEffect(() => {
    if (data?.online?.length > 0 && selectedIds.length === 0 && !list && !isInitializedRef.current) {
      const firstId = data.online[0].id;
      setSelectedIds([firstId]);
      if (onSelectedIdsChange) {
        onSelectedIdsChange([firstId]);
      }
      isInitializedRef.current = true;
    }
  }, [data?.online]);

  // Efecto para sincronizar con list del padre
  useEffect(() => {
    if (list && list.length > 0 && !isInitializedRef.current) {
      setSelectedIds(list);
      isInitializedRef.current = true;
    }
  }, [list]);

  // Efecto para detectar cambio de método
  useEffect(() => {
    const metodoStr = getMetodoString(selectedMetodo);
    if (previousMetodoRef.current && previousMetodoRef.current !== metodoStr) {
      resetAbono();
      consultaKeyRef.current = null;
      isCalculatingRef.current = false;
    }
    previousMetodoRef.current = metodoStr;
  }, [selectedMetodo, resetAbono]);

  // Efecto principal unificado para calcular abono
  useEffect(() => {
    const metodoStr = getMetodoString(selectedMetodo);
    
    // Si no hay método o IDs, resetear
    if (!metodoStr || selectedIds.length === 0) {
      if (!metodoStr) {
        resetAbono();
      }
      return;
    }

    // Si ya hay una ejecución en curso, no hacer nada
    if (isCalculatingRef.current) {
      return;
    }

    // Validar dependencias
    const needsBank = metodoStr === "c2p" || metodoStr === "p2p";
    const hasBank = needsBank ? (selectedBank && selectedBank !== "") : true;
    
    const needsCurrency = metodoStr === "transferencia" || metodoStr === "transfer";
    const hasCurrency = needsCurrency ? (selectedCurrency && selectedCurrency !== "") : true;

    // Si falta alguna dependencia, mostrar loading y esperar
    if ((needsBank && !hasBank) || (needsCurrency && !hasCurrency)) {
      setLoadingResume(false);
      return;
    }

    // Crear clave de consulta para verificar duplicados
    const consultaKey = `${selectedIds.join(',')}_${metodoStr}_${selectedBank || ''}_${selectedCurrency || ''}`;
    
    // Si ya se consultó exactamente esto, no hacer nada
    if (consultaKeyRef.current === consultaKey) {
      return;
    }

    // Si es el primer render o ha pasado suficiente tiempo desde la última ejecución
    const now = Date.now();
    const timeSinceLastExecution = lastExecutionRef.current ? now - lastExecutionRef.current : 9999;
    
    if (timeSinceLastExecution > 500) {
      handleCalculateAbono(selectedIds, selectedMetodo, selectedBank, selectedCurrency);
    } else {
    }
  }, [selectedIds, selectedMetodo, selectedBank, selectedCurrency, handleCalculateAbono, resetAbono]);

  // Efecto para notificar al padre del total
  useEffect(() => {
    if (onTotalChange && selectedBank && selectedBank !== "") {
      onTotalChange(abonar?.total_a_pagar || "0");
    }
  }, [abonar, selectedBank, onTotalChange]);

  const handleCardClick = (facturaId) => {
    if (disabled) {
      setPointer(false);
      return;
    }
    const isChecked = selectedIds.includes(facturaId);
    handleCheckboxChange(facturaId, !isChecked);
  };

  const getMetodoNombre = (metodo) => {
    const metodosMap = {
      c2p: "Pago móvil (C2P)",
      p2p: "Pago móvil (P2P)",
      transfer: "Transferencia bancaria",
      transferencia: "Transferencia bancaria",
      "pago movil": "Pago móvil",
      "pago móvil": "Pago móvil",
    };
    const metodoStr = getMetodoString(metodo);
    if (!metodoStr) return "Método seleccionado";
    return metodosMap[metodoStr.toLowerCase()] || metodoStr;
  };

  const filterAbonosByMetodo = () => {
    if (!abonar?.abonos || abonar.abonos.length === 0 || !selectedMetodo) return [];
    const metodoStr = getMetodoString(selectedMetodo);
    if (!metodoStr) return [];

    const metodoMapping = {
      c2p: ["pago móvil", "pago movil", "c2p"],
      p2p: ["pago móvil", "pago movil", "p2p"],
      transfer: ["transferencia bancaria", "transferencia"],
      transferencia: ["transferencia bancaria", "transferencia"],
      "pago móvil": ["pago móvil", "pago movil", "c2p", "p2p"],
      "pago movil": ["pago móvil", "pago movil", "c2p", "p2p"],
    };

    const posiblesValores = metodoMapping[metodoStr.toLowerCase()] || [metodoStr.toLowerCase()];
    return abonar.abonos.filter((abono) => {
      if (!abono.type) return false;
      const abonoType = abono.type.toLowerCase();
      return posiblesValores.some((valor) => abonoType.includes(valor));
    });
  };

  const getCurrencySymbol = () => {
    const metodoStr = getMetodoString(selectedMetodo);
    if ((metodoStr === "transferencia" || metodoStr === "transfer") && selectedCurrency === "usd") {
      return "$";
    }
    return "Bs.";
  };

  const handleOpenModal = () => {
    if (!selectedMetodo) {
      toast.info("No hay método de pago seleccionado");
      return;
    }
    const abonosFiltrados = filterAbonosByMetodo();
    if (abonosFiltrados.length > 0) {
      setSelectedAbonoInfo(abonosFiltrados);
      setShowModal(true);
    } else {
      toast.info(`No hay abonos disponibles para el método: ${getMetodoNombre(selectedMetodo)}`);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAbonoInfo([]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className={show ? "d-none" : ""}>
      {loading ? (
        <div className="placeholder-glow">
          <div className="d-flex justify-content-between flex-row mb-3">
            <div className="placeholder col-4" style={{ height: 16 }}></div>
            <div className="placeholder col-2" style={{ height: 16 }}></div>
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="border-abonar p-3" style={{ border: "1px solid #e0e0e0", borderRadius: 8, marginBottom: 8 }}>
              <div className="d-flex align-items-center mb-2">
                <div className="placeholder flex-shrink-0 me-3" style={{ width: 20, height: 20, borderRadius: 4 }}></div>
                <div className="flex-grow-1">
                  <div className="placeholder col-6 mb-1" style={{ height: 14 }}></div>
                  <div className="placeholder col-4" style={{ height: 12 }}></div>
                </div>
              </div>
              <div className="placeholder col-5" style={{ height: 12 }}></div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between flex-row mb-3 color-secondary">
            <div>
              <span>Seleccione los servicios a abonar:</span>
            </div>
            <div>
              <strong>Tasa: </strong>
              {data?.tasa} Bs.
            </div>
          </div>
          {data?.online?.map((factura, index) => {
            const isLoading = loadingIcons[factura.id] || false;
            const isSelected = selectedIds.includes(factura.id);

            return (
              <div
                onClick={(e) => {
                  if (e.target.type !== "checkbox" && !disabled) {
                    handleCardClick(factura.id);
                  }
                }}
                className={`border-abonar 
                          ${index === 0 ? "top" : ""} 
                          ${index === data.online.length - 1 ? "bottom" : ""}  
                          ${pointer && "pointer"}
                          ${isSelected ? "selected" : ""}
                          ${disabled ? "disabled" : ""}
                        `}
                key={factura.id || index}
              >
                <div className="col-metodo-2">
                  <div className="col-metodo-2 me-3 me-sm-4">
                    <input
                      className="form-check-abonar"
                      type="checkbox"
                      aria-label="factura"
                      checked={isSelected}
                      onChange={(e) => handleCheckboxChange(factura.id, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="col-metodo">
                    <div className="vertical-align dir-color mb-2 flex-wrap">
                      <div className="font-dir-referencia d-flex align-items-center color-secondary">
                        {factura.tipo_servicio === "Pyme" && <MdStore size={20} className="me-1 flex-shrink-0" />}
                        {factura.tipo_servicio === "Corporativo" && (
                          <MdOutlineFactory size={20} className="me-1 flex-shrink-0" />
                        )}
                        {factura?.tipo_servicio === "ISP" && (
                          <AiOutlineGlobal size={20} className="me-1 flex-shrink-0" />
                        )}
                        <span className="abonar-dir-referencia">{factura?.dir_referencia}</span>
                      </div>
                      <span className="vertical-divider mx-2"></span>
                      <span className="abonar-dir-calle">{factura?.dir_calle}</span>
                    </div>

                    <div className="d-flex align-items-center">
                      <div className="d-flex align-items-center abonar-row-mobile" style={{ flex: 1 }}>
                        <span>
                          {(factura.tipo_servicio === "Corporativo" || factura?.tipo_servicio === "ISP") && (
                            <span className="total-abonar">Total: {formatNumber(factura?.monto)} $</span>
                          )}
                          {factura.tipo_servicio === "Pyme" && (
                            <span className="total-abonar color-secondary">
                              Total: {formatNumber(factura?.monto)} $
                            </span>
                          )}
                        </span>
                        <span className="vertical-divider mx-2"></span>
                        <span className="abonar-plan">
                          {factura.tipo_servicio === "Corporativo" ||
                            (factura?.tipo_servicio === "ISP" && factura?.velocidad !== null) ? (
                            <>
                              Velocidad: {factura?.velocidad} Mbps <MdNetworkCheck className="iconPlan" />
                            </>
                          ) : (
                            `Plan: ${factura?.plan?.nombre}`
                          )}
                        </span>
                        <span className="vertical-divider mx-2"></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Sección de totales */}
          {selectedMetodo && (
            <>
              <h3 className="text-center mt-4 mb-2 color-secondary">
                <strong>A pagar</strong>
              </h3>

              <div className="box-shadow mt-3 summary-pay">
                {loadingResume ? (
                  <div className="d-flex flex-column gap-1 p-3">
                    {[
                      { key: "base", label: "Subtotal", showAlways: true },
                      { key: "iva", label: "IVA", showCondition: (value) => parseFloat(value || 0) > 0 },
                      { key: "igtf", label: "IGTF", showCondition: (value) => parseFloat(value || 0) > 0 },
                      {
                        key: "monto_retenido",
                        label: "A retener",
                        showCondition: (value) => parseFloat(value || 0) > 0,
                      },
                      { key: "comision", label: "Comisión", showCondition: (value) => parseFloat(value || 0) > 0 },
                      {
                        key: "monto_abonado",
                        label: (
                          <div
                            className="d-flex align-items-center gap-2"
                            style={{ cursor: "pointer" }}
                            onClick={handleOpenModal}
                          >
                            <span>Abonado</span>
                            <MdInfo className="me-1 color-primary" size={18} />
                          </div>
                        ),
                        showCondition: (value) => parseFloat(value || 0) > 0,
                      },
                    ].map(({ key, label, showAlways, showCondition }) => {
                      const value = abonar?.[key] || "0.00";
                      const shouldShow = showAlways || (showCondition && showCondition(value));
                      if (!shouldShow) return null;

                      return (
                        <div key={key} className="d-flex flex-row justify-content-between align-items-center py-1">
                          <span>{label}</span>
                          <span>{formatNumber(value)} {getCurrencySymbol()}</span>
                        </div>
                      );
                    })}

                    <hr className="my-2" />

                    <div
                      className="d-flex flex-row justify-content-between pt-1 pb-1 ps-4 pe-4 mt-2 ms-2 me-2"
                      style={{ borderRadius: "30px", backgroundColor: "#6cb42a", color: "#fff" }}
                    >
                      <span className="fw-bold">Total a pagar</span>
                      <span>{formatNumber(abonar.total_a_pagar)} {getCurrencySymbol()}</span>
                    </div>
                  </div>
                ) : (
                  <div className="placeholder-glow p-4" style={{ borderRadius: "30px", backgroundColor: "#fff" }}>
                    <div className="d-flex flex-row justify-content-between mb-2">
                      <div className="placeholder col-3" style={{ height: 14 }}></div>
                      <div className="placeholder col-2" style={{ height: 14 }}></div>
                    </div>
                    <div className="d-flex flex-row justify-content-between mb-2">
                      <div className="placeholder col-2" style={{ height: 14 }}></div>
                      <div className="placeholder col-2" style={{ height: 14 }}></div>
                    </div>
                    <div className="d-flex flex-row justify-content-between mb-3">
                      <div className="placeholder col-3" style={{ height: 14 }}></div>
                      <div className="placeholder col-2" style={{ height: 14 }}></div>
                    </div>
                    <hr />
                    <div className="d-flex flex-row justify-content-between">
                      <div className="placeholder col-4" style={{ height: 18, borderRadius: 30 }}></div>
                      <div className="placeholder col-2" style={{ height: 18, borderRadius: 30 }}></div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Modal con la información de los abonos */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg" scrollable border="0">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            <div className="d-flex align-items-center">
              <span>
                Detalles de Abonos
                {selectedMetodo && (
                  <small className="d-block fs-6 fw-light opacity-75">{getMetodoNombre(selectedMetodo)}</small>
                )}
              </span>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-light p-4">
          {abonar?.abonos && abonar.abonos.length > 0 ? (
            <div className="d-flex flex-column gap-4">
              {abonar.abonos.map((abono, index) => (
                <Card key={abono._id || index} className="border-0 shadow-sm overflow-hidden">
                  <Card.Header className="border-0 pt-3 pb-0">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <h5 className="mb-0 fw-bold text-dark">
                          {abono.type} <span className="text-muted fw-light">#{index + 1}</span>
                        </h5>
                      </div>
                      <span className="badge rounded-pill bg-success px-3 py-2">
                        {abono.ammount ? `${formatNumber(abono.ammount)} Bs.` : "0.00 Bs."}
                      </span>
                    </div>
                  </Card.Header>

                  <Card.Body>
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="d-flex align-items-center">
                          <MdOutlineAccountBalance className="text-muted me-2" size={20} />
                          <div>
                            <small className="text-muted d-block">Banco</small>
                            <span className="fw-semibold">{abono.bank || "No especificado"}</span>
                          </div>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="d-flex align-items-center">
                          <MdOutlineNumbers className="text-muted me-2" size={20} />
                          <div>
                            <small className="text-muted d-block">Referencia</small>
                            <span className="fw-semibold text-break">{abono.reference || "N/A"}</span>
                          </div>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="d-flex align-items-center">
                          <IoCalendarOutline className="text-muted me-2" size={20} />
                          <div>
                            <small className="text-muted d-block">Fecha de Operación</small>
                            <span className="fw-semibold">{formatDate(abono.date)}</span>
                          </div>
                        </div>
                      </Col>

                      <Col xs={12}>
                        <div className="mt-2 pt-2 border-top d-flex align-items-center">
                          <IoFingerPrintOutline className="text-muted me-2" size={16} />
                          <small className="text-muted font-monospace" style={{ fontSize: "0.75rem" }}>
                            ID Interno: {abono._id || "N/A"}
                          </small>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <IoInformationCircleOutline size={50} className="text-muted mb-3 opacity-50" />
              <h5 className="text-muted">No hay información de abonos</h5>
              <p className="small text-secondary">Los detalles aparecerán cuando se registre un pago.</p>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0 bg-light">
          <Button variant="outline-secondary" onClick={handleCloseModal} className="px-4 rounded-pill">
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Abonar;